import {PayloadAction, createSelector, createSlice} from '@reduxjs/toolkit';

import {RootState} from '..';

import {clearUser, registrationCreated} from '../actions';

import {UserDetails, endpoints as userApiEndpoints} from './userAPI';
import {endpoints as candidateApiEndpoints} from './../candidate/candidateAPI';

export interface UserState {
    isLoading: boolean;
    user: UserDetails | undefined;
    reLoginRequired: boolean;
    afterLoginState: 'login' | 'signup' | undefined;
}

const initialState: UserState = {
    isLoading: false,
    user: undefined,
    reLoginRequired: false,
    afterLoginState: undefined,
};

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setAfterLoginState: (state, action: PayloadAction<'login' | 'signup'>) => {
            state.afterLoginState = action.payload;
        },
        clearAfterLoginState: (state) => {
            state.afterLoginState = undefined;
            state.user = undefined;
        },
        hideChildMigrationIsAvailableNotificationBar: (state) => {
            state.user!.hideNotificationAboutAvailableMigration = true;
        },
        hideShowInboxNotificationBar: (state) => {
            state.user!.hideShowInboxNotificationBar = true;
        },
        hideShowPrsNotificationBar: (state) => {
            state.user!.hideShowPrsNotificationBar = true;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(registrationCreated, (state) => {
                state.reLoginRequired = true;
            })
            .addCase(clearUser, (state) => {
                return {
                    ...state,
                    user: undefined,
                };
            })
            .addMatcher(userApiEndpoints.getDetails.matchPending, (state) => {
                state.isLoading = true;
            })
            .addMatcher(userApiEndpoints.getDetails.matchFulfilled, (state, {payload}) => {
                return {
                    ...state,
                    user: payload,
                    isLoading: false,
                };
            })
            .addMatcher(userApiEndpoints.getDetails.matchRejected, (state) => {
                state.isLoading = false;
            })
            .addMatcher(candidateApiEndpoints.childRequireMigration.matchFulfilled, (state, action) => {
                if (state.user) {
                    state.user.hasChildWhoRequireMigration = action.payload;
                }
            });
    },
});

export const {
    setAfterLoginState,
    clearAfterLoginState,
    hideChildMigrationIsAvailableNotificationBar,
    hideShowInboxNotificationBar,
    hideShowPrsNotificationBar,
} = userSlice.actions;

export const selectUserState = (state: RootState) => state.user;
export const selectUser = createSelector(selectUserState, (state) => state.user);
export const selectIsLoggedUser = createSelector(selectUser, (user) => !!user);
export const selectIsUserReLoginRequired = createSelector(selectUserState, (state) => state.reLoginRequired);
export const selectAfterLoginState = createSelector(selectUserState, (state) => state.afterLoginState);

export default userSlice.reducer;
